import React from 'react';
import type { FileInputOnSelectPayload } from '../../../../personalisation/src/components/FileInput/file-input-props';
import { FileInput } from '../../../../personalisation/src/components/FileInput';
import { FaceTabs } from '@uv-inserts/components/FaceTabs';
import { DevFaceVisualisers } from '@uv-inserts/dev/DevFaceVisualisers';
import { useUserInput } from '../../context/UserInputContext';
import { useUvInsertVisualiserPageViewModel } from './UvInsertVisualiserPage.vm';
import styles from '../../components/GiftWrapModal/InsertsPages/insertsPages.module.css';

type UvInsertVisualiserPageProps = {
  /** Current route name — used to look up config handle and product key. */
  routeName: string;
};

/**
 * Config-driven visualiser page that replaces all 9 legacy hardcoded visualiser components.
 * Renders FaceTabs, config-driven faces via DevFaceVisualisers, and FileInput controls
 * for any IMAGE_UPLOAD elements on the active tab.
 *
 * The UvInsertChooser (theme/suggestion panels) is rendered separately at the modal
 * layout level by UvInsertChooserSlot in router.tsx — keeping it outside the scrollable
 * content area so it opens upward without requiring the user to scroll.
 */
export function UvInsertVisualiserPage({ routeName }: UvInsertVisualiserPageProps) {
  const userInput = useUserInput();

  const vm = useUvInsertVisualiserPageViewModel({ routeName, userInput });

  if (!vm.config || !vm.resolved) {
    return null;
  }

  return (
    <div className={styles['page--container']}>
      <FaceTabs
        tabs={vm.faceTabItems}
        activeIndex={vm.activeTabIndex}
        onActiveIndexChange={vm.setActiveTabIndex}
        fullWidth
      />

      <DevFaceVisualisers
        facesToShow={vm.facesToShow}
        selectedThemeImageSrc={vm.selectedThemeImageSrc}
        textByKey={vm.textByKey}
        setText={vm.setText}
        imageByKey={vm.imageByKey}
        setImageTransformByKey={vm.setImageTransformByKey}
        imageTransformByKey={vm.imageTransformByKey}
      />

      {vm.imageUploadKeysForActiveTab.length > 0 && (
        <div className={styles['file-input-container']}>
          {vm.imageUploadKeysForActiveTab.map((imageKey) => (
            <FileInput
              key={imageKey}
              label={vm.imageUploadKeysForActiveTab.length === 1 ? 'Upload a Photo' : `Upload (${imageKey})`}
              onFileSelect={(payload: FileInputOnSelectPayload) => vm.handleImageSelect(imageKey, payload)}
              accept="png,jpg,jpeg,webp"
              disabled={false}
              buttonVariant="primary"
              buttonColor="orange"
              buttonSize="small"
              maxFileSize={5 * 1024 * 1024}
              id={`uv-insert-img-${imageKey}`}
              selectedFile={vm.imageByKey[imageKey] ?? null}
            />
          ))}
        </div>
      )}
    </div>
  );
}
